// src/components/bot/apiExecutor.js
// ─────────────────────────────────────────────────────────────────────────────
// Layer 5 — API Executor
//
// Responsibilities:
//   • Normalize endpoint paths (strip any leading api/v1/ prefix, ensure leading /).
//   • Inject query parameters into the URL.
//   • Execute the HTTP call via the existing botFetch() helper.
//   • Return a uniform { ok, data, error } result — never throws.
//
// This is the only place where URL normalization logic lives.
// Previously this logic was duplicated inside InternalBotV2 and InternalBot.
// ─────────────────────────────────────────────────────────────────────────────
import { botFetch } from './botApi';

// ── Internal helpers ──────────────────────────────────────────────────────────

/**
 * Strip any absolute URL or api/v1 prefix so botFetch() receives a clean
 * relative path like "/chat-boat/today-attendance".
 *
 * Handles all of these:
 *   "chat-boat/today-attendance"
 *   "/chat-boat/today-attendance"
 *   "api/v1/chat-boat/today-attendance"
 *   "https://api.example.com/api/v1/chat-boat/today-attendance"
 */
function normalizeEndpoint(raw) {
  let url = String(raw || '').trim();

  // Strip full URL (e.g. https://api.iotroncs.com/api/v1/...)
  const apiV1Match = url.match(/\/api\/v1\/(.*)/i);
  if (apiV1Match) {
    url = apiV1Match[1];
  }

  // Strip bare "api/v1/" prefix without leading slash
  if (/^api\/v1\//i.test(url)) {
    url = url.replace(/^api\/v1\//i, '');
  }

  // Ensure leading slash for botFetch's relative URL handling
  if (!url.startsWith('/')) {
    url = '/' + url;
  }

  return url;
}

/**
 * Append query parameters to a URL.
 * Preserves any existing query params already in the URL string.
 * Skips null, undefined, and empty-string values.
 *
 * @param {string} url    - Base URL (may already contain a query string)
 * @param {object} params - Additional params to add
 * @returns {string}
 */
function injectParams(url, params) {
  if (!params || !Object.keys(params).length) return url;

  const [base, existing] = url.split('?');
  const search = new URLSearchParams(existing || '');

  for (const [k, v] of Object.entries(params)) {
    if (v !== null && v !== undefined && v !== '') {
      search.set(k, String(v));
    }
  }

  const qs = search.toString();
  return qs ? `${base}?${qs}` : base;
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Execute a planned backend API call.
 *
 * @param {string} endpoint - Raw endpoint from the AI Planner (may contain prefix)
 * @param {object} params   - Query parameters to inject (from the planner)
 * @returns {Promise<{ ok: boolean, data: any, error: string | null }>}
 */
export async function execute(endpoint, params = {}) {
  try {
    const normalized = normalizeEndpoint(endpoint);
    const url        = injectParams(normalized, params);

    const r = await botFetch(url);

    if (!r.ok) {
      return { ok: false, data: null, error: r.message || 'Request failed' };
    }

    return { ok: true, data: r.data, error: null };
  } catch (e) {
    return { ok: false, data: null, error: e.message || 'Unknown error' };
  }
}
